import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Briefcase, Building2, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api';

type CaseResponse = { id: string };

export function NewCasePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<'type' | 'details'>('type');
  const [workflowType, setWorkflowType] = useState<string>('');
  const [title, setTitle] = useState('');
  const [reference, setReference] = useState('');

  const createCase = useMutation<CaseResponse, Error, void>({
    mutationFn: () =>
      apiClient.post<CaseResponse>('/cases', {
        title,
        reference_number: reference || null,
        workflow_type: workflowType,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      navigate(`/business/cases/${created.id}`, { replace: true });
    },
  });

  if (step === 'type') {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-navy mb-2">פתיחת קייס חדש</h1>
        <p className="text-slate-600 mb-8">בחר את סוג הניתוח</p>

        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              setWorkflowType('group_review');
              setStep('details');
            }}
            className="card text-right hover:shadow-md hover:border-business-primary transition"
          >
            <Briefcase className="w-10 h-10 text-business-primary mb-4" />
            <h3 className="text-xl font-semibold text-navy mb-2">
              ניתוח קבוצת חברות
            </h3>
            <p className="text-sm text-slate-600">
              מיפוי חשיפות, ערבויות, אובליגו ואיתנות פיננסית על פני 2-5 חברות בקבוצה. היסטוריה 3 שנים, ניתוח נורמטיבי, זיהוי ריכוזים.
            </p>
            <div className="mt-4 text-xs text-slate-500">
              מתאים ל: לקוחות קיימים, שיפור תנאים, ניהול שוטף
            </div>
          </button>

          <button
            onClick={() => {
              setWorkflowType('deal_feasibility');
              setStep('details');
            }}
            className="card text-right hover:shadow-md hover:border-business-primary transition"
          >
            <Building2 className="w-10 h-10 text-business-primary mb-4" />
            <h3 className="text-xl font-semibold text-navy mb-2">
              ניתוח עסקה חדשה
            </h3>
            <p className="text-sm text-slate-600">
              בדיקת כדאיות עסקה: רכישת נכס מניב, מימון ציוד, גיוס הון חוזר. תחזית DSCR 25 שנה, השוואת בנקים, בחינת ערבויות ובטוחות.
            </p>
            <div className="mt-4 text-xs text-slate-500">
              מתאים ל: עסקאות חדשות, החלטות השקעה, בניית תיק
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-navy mb-2">פרטי הקייס</h1>
      <p className="text-slate-600 mb-8">
        {workflowType === 'group_review' ? 'ניתוח קבוצתי' : 'ניתוח עסקה'}
      </p>

      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            כותרת הקייס
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="לדוגמה: קבוצת חברות אברהמי"
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            מספר סימוכין (אופציונלי)
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="לדוגמה: 2026-0001"
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
          />
        </div>

        {createCase.isError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>שגיאה ביצירת הקייס: {createCase.error.message}</span>
          </div>
        )}

        <div className="flex justify-between pt-4">
          <button
            onClick={() => setStep('type')}
            disabled={createCase.isPending}
            className="btn-secondary disabled:opacity-50"
          >
            חזרה
          </button>
          <button
            onClick={() => createCase.mutate()}
            disabled={!title || createCase.isPending}
            className="btn-primary disabled:opacity-50 inline-flex items-center gap-2"
          >
            {createCase.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {createCase.isPending ? 'יוצר...' : 'יצירת הקייס'}
          </button>
        </div>
      </div>
    </div>
  );
}
